import { getDatabase, ref, get, set, remove } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { NotificationRule } from '../types/notifications';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { recordActivity } from './activityApi';

const RULES_PATH = 'config/notificationRules/issues';

export class NotificationRulesApi {
    private db = getDatabase();

    /**
     * Parse and validate a notification rule from Firebase data
     */
    private parseRule(data: unknown): NotificationRule | null {
        if (!data || typeof data !== 'object') return null;
        const d = data as Record<string, unknown>;

        if (typeof d.userId !== 'string') return null;

        return {
            userId: d.userId,
            buildingTypes: Array.isArray(d.buildingTypes) ? d.buildingTypes : [],
            buildings: Array.isArray(d.buildings) ? d.buildings : [],
            createdBy: typeof d.createdBy === 'string' ? d.createdBy : '',
            createdAt: typeof d.createdAt === 'string' ? d.createdAt : '',
            updatedBy: typeof d.updatedBy === 'string' ? d.updatedBy : undefined,
            updatedAt: typeof d.updatedAt === 'string' ? d.updatedAt : undefined,
        };
    }

    /**
     * Fetch all notification rules
     */
    async getRules(): Promise<NotificationRule[]> {
        const rulesRef = ref(this.db, RULES_PATH);
        const snapshot = await get(rulesRef);

        if (!snapshot.exists()) {
            return [];
        }

        const data = snapshot.val() as Record<string, unknown>;
        const rules: NotificationRule[] = [];

        for (const key in data) {
            const rule = this.parseRule(data[key]);
            if (rule) {
                rules.push(rule);
            }
        }

        return rules;
    }

    /**
     * Get a single notification rule by userId
     */
    async getRule(userId: string): Promise<NotificationRule | null> {
        const ruleRef = ref(this.db, `${RULES_PATH}/${userId}`);
        const snapshot = await get(ruleRef);

        if (!snapshot.exists()) {
            return null;
        }

        return this.parseRule(snapshot.val());
    }

    /**
     * Create a new notification rule
     * Uses userId as the key to enforce one rule per user
     */
    async createRule(
        userId: string,
        buildingTypes: string[],
        buildings: string[]
    ): Promise<NotificationRule> {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad');
        }

        if (!userId) {
            throw new Error('Användare krävs');
        }

        if (buildingTypes.length === 0 && buildings.length === 0) {
            throw new Error('Minst en byggnadstyp eller byggnad måste anges');
        }

        // Check if rule already exists for this user
        const existingRule = await this.getRule(userId);
        if (existingRule) {
            throw new Error('Denna användare har redan en aviseringsregel. Redigera den istället.');
        }

        const ruleRef = ref(this.db, `${RULES_PATH}/${userId}`);
        const now = formatDateForStorage(new Date());

        const rule: NotificationRule = {
            userId,
            buildingTypes,
            buildings,
            createdBy: user.uid,
            createdAt: now,
        };

        await set(ruleRef, rule);
        recordActivity('settings.updated');
        return rule;
    }

    /**
     * Update an existing notification rule
     */
    async updateRule(
        userId: string,
        buildingTypes: string[],
        buildings: string[]
    ): Promise<void> {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad');
        }

        if (buildingTypes.length === 0 && buildings.length === 0) {
            throw new Error('Minst en byggnadstyp eller byggnad måste anges');
        }

        // Get existing rule
        const existingRule = await this.getRule(userId);
        if (!existingRule) {
            throw new Error('Regeln hittades inte');
        }

        const now = formatDateForStorage(new Date());

        const updatedRule: NotificationRule = {
            ...existingRule,
            buildingTypes,
            buildings,
            updatedBy: user.uid,
            updatedAt: now,
        };

        const ruleRef = ref(this.db, `${RULES_PATH}/${userId}`);
        await set(ruleRef, updatedRule);
        recordActivity('settings.updated');
    }

    /**
     * Delete a notification rule
     */
    async deleteRule(userId: string): Promise<void> {
        const user = getAuth().currentUser;
        if (!user) {
            throw new Error('Du måste vara inloggad');
        }

        const ruleRef = ref(this.db, `${RULES_PATH}/${userId}`);
        await remove(ruleRef);
        recordActivity('settings.updated');
    }
}

// Export singleton instance
export const notificationRulesApi = new NotificationRulesApi();
